import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, Play } from "lucide-react";
import { toast } from "sonner";

const KINDS = ["before", "after", "video"];

export default function Portfolio() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/portfolio").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/portfolio/upload", fd, {
        params: { title: file.name, kind: file.type.startsWith("video") ? "video" : "after", service: "detailing" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (id) => {
    try { await api.delete(`/portfolio/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = filter === "all" ? items : items.filter((x) => x.kind === filter);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="portfolio-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#FFB800]">Portfolio</div>
          <h1 className="font-display text-4xl mt-2">Before, after & everything in between.</h1>
        </div>
        {user?.role === "admin" && (
          <label className="btn-primary cursor-pointer" data-testid="portfolio-upload-label">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload media"}
            <input type="file" accept="image/*,video/*" hidden onChange={upload} data-testid="portfolio-upload-input" />
          </label>
        )}
      </div>

      <div className="mt-8 flex gap-2">
        {["all", ...KINDS].map((k) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider border ${filter === k ? "border-[#FFB800] text-[#FFB800]" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
            {k}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-16 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-lg p-16">
          No media yet. {user?.role === "admin" ? "Upload your first before/after shot." : "Check back soon."}
        </div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((it, i) => (
            <div key={it.id} data-testid={`portfolio-item-${it.id}`}
              className={`card-surface rounded-lg overflow-hidden group ${i % 5 === 0 ? "sm:col-span-2 sm:row-span-2" : ""}`}>
              <div className="aspect-square bg-black relative">
                {it.content_type?.startsWith("video") ? (
                  <video src={fileUrl(it.storage_path)} className="h-full w-full object-cover" controls />
                ) : (
                  <img src={fileUrl(it.storage_path)} alt={it.title} className="h-full w-full object-cover" />
                )}
                <div className="absolute top-2 left-2 overline bg-black/60 px-2 py-1 rounded">{it.kind}</div>
                {user?.role === "admin" && (
                  <button onClick={() => del(it.id)} data-testid={`portfolio-delete-${it.id}`}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </button>
                )}
              </div>
              <div className="p-3 text-xs">
                <div className="truncate">{it.title}</div>
                <div className="text-zinc-500 mt-1">{new Date(it.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
